import { z } from 'zod';
import { allItemsResponse, optionalCuid, decimalString } from './common';

/**
 * Schemas for the per-project Panel line items.
 *
 * Path: `/projects/:projectId/panels`
 *
 * Snapshot semantics:
 * - When created `from-catalog`, the line item copies name/sku/dimensions/
 *   material/color from the catalog item and sets `unitPriceNet`/`currency`
 *   from the request body (price is workshop-set, not catalog-cost).
 * - `catalogItemId` is recorded for traceability and is **immutable** once set.
 * - Subsequent edits to the catalog item do NOT propagate to existing lines.
 *
 * Worker access:
 * - Workers may LIST/GET panels only on projects they are assigned to.
 * - Workers see no `unitPriceNet`/`currency` (financials hidden).
 * - Workers may not mutate (`PROJECT_ITEMS_CREATE/UPDATE/DELETE`).
 *
 * Status locking:
 * - Mutations are forbidden when the parent project is in a terminal status
 *   (`DELIVERED` or `REJECTED`).
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});

const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});

const positiveIntegerDecimal = decimalString.refine(
  (v) => Number.isInteger(Number(v)) && Number(v) > 0,
  {
    message: 'Must be a positive integer',
  },
);

const dimMm = z.number().int().positive().max(100_000);
const optionalDimMm = z.number().int().positive().max(100_000).optional();

const panelCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type PanelCurrencyValue = z.infer<typeof panelCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const panelListItem = z.object({
  id: z.string(),
  projectId: z.string(),
  roomId: z.string().cuid().nullable(),
  areaId: z.string().cuid().nullable(),
  catalogItemId: z.string().nullable(),
  name: z.string(),
  sku: z.string().nullable(),
  widthMm: z.number().int(),
  heightMm: z.number().int(),
  thicknessMm: z.number().int(),
  material: z.string().nullable(),
  /**
   * The board(s) as the item tables show them, by the shared rule the quote PDF
   * uses (`formatMaterials`): the saved label when there is one, else the
   * catalog name. Filled by the list endpoints, which load the names; absent
   * on single-item responses. '' when the item carries no material.
   */
  materialDisplay: z.string().optional(),
  edging: z.string().nullable(),
  doubled: z.boolean(),
  quantity: decimalString,
  coefficient: decimalString,
  materialCoefficient: decimalString,
  // Financial fields are absent for callers without PROJECTS_READ_FINANCIALS.
  unitPriceNet: decimalString.optional(),
  /**
   * Unit price WITHOUT the markup: MANUAL rows repeat `unitPriceNet`; AUTO rows
   * report the engine's catalog-cost sum (`subtotalBaseNet`), absent until a
   * breakdown stored before that field existed is recomputed. Absent when
   * finances are hidden.
   */
  unitPriceBaseNet: decimalString.optional(),
  /**
   * Unit price WITH the markup: `unitPriceNet` times the row coefficient. On
   * AUTO rows the markups already live inside `unitPriceNet`, so the two agree
   * there by construction. Absent when finances are hidden.
   */
  unitPriceFinalNet: decimalString.optional(),
  currency: panelCurrencyEnum.optional(),
  // Server-computed: quantity * coefficient * unitPriceNet. Absent when finances hidden.
  totalNet: decimalString.optional(),
  // Pricing engine fields (PRICING_ENGINE_PLAN E.3).
  pricingMode: z.enum(['MANUAL', 'AUTO']),
  materialId: z.string().nullable(),
  edgeId: z.string().nullable(),
  frontMaterialId: z.string().nullable(),
  frontEdgeId: z.string().nullable(),
  cx2: z.boolean(),
  glassSanding: z.boolean(),
  computedBreakdown: z.unknown().nullable().optional(),
  pricingError: z.string().optional(),
  orderIndex: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PanelListItem = z.infer<typeof panelListItem>;

export const panelDetails = panelListItem;
export type PanelDetails = z.infer<typeof panelDetails>;

export const listPanelsResponse = allItemsResponse(panelListItem);
export type ListPanelsResponse = z.infer<typeof listPanelsResponse>;

// ----- Manual create (no catalog reference) -----
export const createPanelRequest = z.object({
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  name: z.string().trim().min(1).max(200),
  sku: optionalTrim(50),
  widthMm: dimMm,
  heightMm: dimMm,
  thicknessMm: dimMm,
  material: optionalTrim(100),
  edging: optionalTrim(100),
  doubled: z.boolean().default(false),
  quantity: positiveIntegerDecimal.default('1'),
  coefficient: positiveDecimal.default('2.5'),
  /**
   * Markup for THIS use of the material, seeded from `Material.coefficient`.
   * On AUTO rows it is the only markup — `coefficient` above is pinned to 1,
   * because the number belongs to the board and not to the row.
   */
  materialCoefficient: positiveDecimal.optional(),
  unitPriceNet: nonNegativeDecimal.default('0.00'),
  currency: panelCurrencyEnum.default('RON'),
  notes: optionalTrim(2000),
  // Pricing-engine inputs (optional). Send `pricingMode: 'AUTO'` together with
  // `materialId` to have the server compute `unitPriceNet` from the pure
  // pricing engine. `edgeId` is optional and prices as zero edge cost when omitted.
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  materialId: z.string().cuid().optional().nullable(),
  edgeId: z.string().cuid().optional().nullable(),
  frontMaterialId: z.string().cuid().optional().nullable(),
  frontEdgeId: z.string().cuid().optional().nullable(),
  cx2: z.boolean().optional(),
  glassSanding: z.boolean().optional(),
});
export type CreatePanelRequest = z.infer<typeof createPanelRequest>;

// ----- Create from a CatalogItem (snapshot) -----
//
// Required fields the catalog item may not have are surfaced via `overrides`.
// Unit price MUST be set explicitly here — catalog stores cost, line items
// store sell price, and the API does not assume a markup.
export const createPanelFromCatalogRequest = z.object({
  catalogItemId: z.string().min(1),
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  unitPriceNet: nonNegativeDecimal,
  currency: panelCurrencyEnum.optional(),
  quantity: positiveIntegerDecimal.optional(),
  coefficient: positiveDecimal.optional(),
  materialCoefficient: positiveDecimal.optional(),
  doubled: z.boolean().optional(),
  notes: optionalTrim(2000),
  overrides: z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      sku: optionalTrim(50),
      widthMm: optionalDimMm,
      heightMm: optionalDimMm,
      thicknessMm: optionalDimMm,
      material: optionalTrim(100),
      edging: optionalTrim(100),
      pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
      materialId: z.string().cuid().optional().nullable(),
      edgeId: z.string().cuid().optional().nullable(),
      frontMaterialId: z.string().cuid().optional().nullable(),
      frontEdgeId: z.string().cuid().optional().nullable(),
      doubled: z.boolean().optional(),
      cx2: z.boolean().optional(),
      glassSanding: z.boolean().optional(),
    })
    .optional(),
});
export type CreatePanelFromCatalogRequest = z.infer<typeof createPanelFromCatalogRequest>;

// ----- Update (partial) -----
//
// `catalogItemId` is intentionally not patchable — the snapshot link is
// permanent. `projectId` is taken from the URL.
export const updatePanelRequest = z
  .object({
    roomId: z.string().cuid().nullable(),
    areaId: z.string().cuid().nullable(),
    name: z.string().trim().min(1).max(200),
    sku: optionalTrim(50),
    widthMm: dimMm,
    heightMm: dimMm,
    thicknessMm: dimMm,
    material: optionalTrim(100),
    edging: optionalTrim(100),
    doubled: z.boolean(),
    quantity: positiveIntegerDecimal,
    coefficient: positiveDecimal,
    materialCoefficient: positiveDecimal,
    unitPriceNet: nonNegativeDecimal,
    currency: panelCurrencyEnum,
    orderIndex: z.number().int().min(0),
    notes: optionalTrim(2000),
    pricingMode: z.enum(['MANUAL', 'AUTO']),
    materialId: z.string().cuid().nullable(),
    edgeId: z.string().cuid().nullable(),
    frontMaterialId: z.string().cuid().nullable(),
    frontEdgeId: z.string().cuid().nullable(),
    cx2: z.boolean(),
    glassSanding: z.boolean(),
  })
  .partial();
export type UpdatePanelRequest = z.infer<typeof updatePanelRequest>;
